// apps/client/src/components/widgets/WhoToCallWidget.tsx
// Collapsible accordion widget for the main dashboard.
// Shows trail drop-off alerts sorted by severity + run-off health badge.
// Collapsed by default — visual cues (accent bar, ping, dollar amount) draw attention.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ChevronDown, Check, AlertTriangle } from 'lucide-react';
import { useWhoToCall } from '../../hooks/useWhoToCall';
import { useTrailRunOffPercentage, type RunOffHealth } from '../../hooks/useTrailRunOffPercentage';
import { formatCurrencyShorthand } from '../../utils/formatters';
import { humaniseAlertType } from '../../utils/alertTypeLabel';

interface WhoToCallWidgetProps {
  companyId: string;
  /** Scope alerts to a single office */
  officeId?: string;
  /** Hide dollar amounts for hideCommissions brokers */
  hideCommissions?: boolean;
}

// --- Style helpers keyed by health status ---

const HEALTH_CONFIG: Record<RunOffHealth, {
  accentClass: string;
  borderClass: string;
  glowClass: string;
  dotBg: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  labelText: string;
  label: string;
}> = {
  healthy: {
    accentClass: 'shadow-[inset_3px_0_0_0_rgba(16,185,129,0.4)]',
    borderClass: 'border-gray-700/60',
    glowClass: '',
    dotBg: 'bg-emerald-400',
    badgeBg: 'bg-emerald-500/10',
    badgeBorder: 'border-emerald-500/20',
    badgeText: 'text-emerald-400',
    labelText: 'text-emerald-400/60',
    label: 'Healthy',
  },
  warning: {
    accentClass: 'shadow-[inset_3px_0_0_0_rgba(245,158,11,0.6)]',
    borderClass: 'border-gray-700/60',
    glowClass: '',
    dotBg: 'bg-amber-400',
    badgeBg: 'bg-amber-500/10',
    badgeBorder: 'border-amber-500/20',
    badgeText: 'text-amber-400',
    labelText: 'text-amber-400/60',
    label: 'Warning',
  },
  critical: {
    accentClass: 'shadow-[inset_3px_0_0_0_rgba(239,68,68,0.6)]',
    borderClass: 'border-red-500/20',
    glowClass: 'animate-[glow-red_3s_ease-in-out_infinite]',
    dotBg: 'bg-red-500',
    badgeBg: 'bg-red-500/10',
    badgeBorder: 'border-red-500/25',
    badgeText: 'text-red-400',
    labelText: 'text-red-400/60',
    label: 'Critical',
  },
};

const ALERT_DISPLAY: Record<string, {
  dotColor: string;
  label: string;
  labelColor: string;
  valueColor: string;
}> = {
  current_month_dropoff: {
    dotColor: 'bg-red-500',
    label: 'Urgent',
    labelColor: 'text-red-400',
    valueColor: 'text-red-400',
  },
  previous_months_dropoff: {
    dotColor: 'bg-blue-500',
    label: 'Churned',
    labelColor: 'text-blue-400',
    valueColor: 'text-blue-400',
  },
  historical_dropoff: {
    dotColor: 'bg-orange-500',
    label: 'Unresolved',
    labelColor: 'text-orange-400',
    valueColor: 'text-orange-400',
  },
  significant_variance: {
    dotColor: 'bg-amber-500',
    label: 'Warning',
    labelColor: 'text-amber-400',
    valueColor: 'text-amber-400',
  },
};

// Any alert type the backend adds ahead of the UI renders as a neutral chip whose
// text is DERIVED from the tier's own name. A constant here ("Attention", "Unknown")
// would discard information the row already carries and tell the broker nothing.
const alertDisplayFallback = (alertType: string) => ({
  dotColor: 'bg-gray-500',
  label: humaniseAlertType(alertType),
  labelColor: 'text-gray-400',
  valueColor: 'text-gray-300',
});

const WhoToCallWidget: React.FC<WhoToCallWidgetProps> = ({ companyId, officeId, hideCommissions = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const {
    records,
    totalAlerts,
    totalAnnualRiskCents,
    urgentCount,
    churnedCount,
    earlierUnresolvedCount,
    warningCount,
    loading: loadingAlerts,
    hasIncompleteData,
    incompleteDataWarning,
  } = useWhoToCall({ companyId, officeId, limit: 5 });

  const {
    runOffPercentage,
    health,
    periodLabel,
    loading: loadingRunOff,
  } = useTrailRunOffPercentage({ companyId, officeId });

  const loading = loadingAlerts || loadingRunOff;
  const hasAlerts = totalAlerts > 0;

  // Widget accent/border follows run-off health status, not alert count
  const cfg = HEALTH_CONFIG[health || 'healthy'];
  const accentClass = cfg.accentClass;

  if (loading) {
    // Skeleton loader matching collapsed height
    return (
      <div className="bg-gray-800/80 border border-gray-700/60 rounded-xl overflow-hidden animate-pulse">
        <div className="px-5 py-3.5 flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-gray-700" />
          <div className="h-4 w-24 bg-gray-700 rounded" />
          <div className="h-4 w-16 bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Inject keyframe for critical glow (only CSS needed beyond Tailwind) */}
      {health === 'critical' && (
        <style>{`
          @keyframes glow-red {
            0%, 100% { border-color: rgba(239, 68, 68, 0.2); }
            50% { border-color: rgba(239, 68, 68, 0.45); }
          }
        `}</style>
      )}

      <div
        className={`bg-gray-800/80 border rounded-xl overflow-hidden ${cfg.borderClass} ${accentClass} ${cfg.glowClass}`}
      >
        {/* Collapsed header — always visible */}
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-full px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-gray-700/30 transition-colors cursor-pointer text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            {/* Icon — color matches run-off health status */}
            {hasAlerts ? (
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                health === 'critical'
                  ? 'bg-red-500/15 border border-red-500/25'
                  : health === 'warning'
                    ? 'bg-amber-500/15 border border-amber-500/25'
                    : 'bg-emerald-500/15 border border-emerald-500/20'
              }`}>
                <Phone className={`w-3.5 h-3.5 ${
                  health === 'critical' ? 'text-red-400'
                    : health === 'warning' ? 'text-amber-400'
                    : 'text-emerald-400'
                }`} />
              </div>
            ) : (
              <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
              </div>
            )}

            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold text-white">Who To Call</span>
              <span className="text-[10px] text-gray-500">Trail drop-offs</span>
            </div>

            {/* Alert count with ping */}
            {hasAlerts ? (
              <>
                <div className="relative flex-shrink-0">
                  <div className="relative w-2 h-2 rounded-full bg-red-500">
                    <span className="absolute inset-0 rounded-full bg-red-500/50 animate-ping" />
                  </div>
                </div>
                <span className="text-xs font-medium text-red-400">
                  {totalAlerts} alert{totalAlerts !== 1 ? 's' : ''}
                </span>

                {/* $ at risk */}
                {!hideCommissions && (
                  <>
                    <span className="text-gray-600 text-xs hidden sm:inline">&middot;</span>
                    <span className="text-xs text-red-400/80 hidden sm:inline">
                      {formatCurrencyShorthand(totalAnnualRiskCents)}/yr at risk
                    </span>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-2 py-0.5">
                <span className="text-[11px] font-medium text-emerald-400">No alerts</span>
              </div>
            )}

            {/* Run-off badge */}
            {runOffPercentage !== null && (
              <>
                <span className="text-gray-600 text-xs hidden md:inline">&middot;</span>
                <div className={`hidden md:flex items-center gap-1.5 ${cfg.badgeBg} border ${cfg.badgeBorder} rounded-md px-2 py-0.5`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${cfg.dotBg}`} />
                  <span className={`text-[11px] font-medium ${cfg.badgeText}`}>
                    {runOffPercentage.toFixed(1)}%
                  </span>
                  {health !== 'healthy' && (
                    <span className={`text-[9px] uppercase font-semibold tracking-wider ${cfg.labelText}`}>
                      {cfg.label}
                    </span>
                  )}
                </div>
              </>
            )}

            {/* Incomplete data indicator (collapsed) */}
            {hasIncompleteData && (
              <>
                <span className="text-gray-600 text-xs hidden sm:inline">&middot;</span>
                <div className="hidden sm:flex items-center gap-1 bg-amber-500/10 border border-amber-500/20 rounded-md px-1.5 py-0.5">
                  <AlertTriangle className="w-2.5 h-2.5 text-amber-400" />
                  <span className="text-[10px] text-amber-400 font-medium">Partial</span>
                </div>
              </>
            )}

            {/* Period label */}
            <span className="text-gray-600 text-xs hidden lg:inline">&middot;</span>
            <span className="text-[11px] text-gray-500 hidden lg:inline">{periodLabel}</span>
          </div>

          {/* Chevron */}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Expandable body */}
        <div
          className="overflow-hidden transition-[max-height] duration-350 ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{ maxHeight: isOpen ? '600px' : '0px' }}
        >
          <div className="border-t border-gray-700/40">
            {/* Incomplete data warning banner */}
            {hasIncompleteData && incompleteDataWarning && (
              <div className="px-5 py-2 bg-amber-500/5 border-b border-amber-500/10 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                <span className="text-xs text-amber-400/90">{incompleteDataWarning}</span>
              </div>
            )}
            {hasAlerts ? (
              <>
                {/* Call list rows */}
                <div className="divide-y divide-gray-700/30">
                  {records.map((record) => {
                    const display = ALERT_DISPLAY[record.alertType] ?? alertDisplayFallback(record.alertType);
                    const isDropOff = record.alertType !== 'significant_variance';

                    return (
                      <div
                        key={`${record.loanId}-${record.paymentMonth}`}
                        className="px-5 py-3 flex items-center gap-4 hover:bg-gray-700/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/reports/trail-drop-off?loanId=${encodeURIComponent(record.loanId)}`)}
                      >
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className={`w-2 h-2 rounded-full ${display.dotColor} flex-shrink-0`} />
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${display.labelColor} w-16`}>
                            {display.label}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white truncate">
                              {record.customerName || 'Unknown Client'}
                            </span>
                            {record.lender && (
                              <>
                                <span className="text-xs text-gray-500">&middot;</span>
                                <span className="text-xs text-gray-400 truncate">{record.lender}</span>
                              </>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Broker: {record.brokerDisplay} &middot; {record.loanId}
                          </div>
                        </div>

                        {!hideCommissions && (
                          <div className="text-right flex-shrink-0">
                            {isDropOff ? (
                              <>
                                <div className={`text-sm font-semibold ${display.valueColor}`}>
                                  -{formatCurrencyShorthand(record.dollarImpactCents)}
                                  <span className={`text-xs font-normal ${display.valueColor} opacity-70`}>/mo</span>
                                </div>
                                <div className="text-[10px] text-gray-500">
                                  {record.alertType === 'current_month_dropoff'
                                    ? 'Trail went to $0'
                                    : `No trail for ${record.paymentMonth ? '2+' : ''} months`}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className={`text-sm font-semibold ${display.valueColor}`}>
                                  &#9660; {Math.abs(record.variancePercent || 0).toFixed(0)}%
                                </div>
                                <div className="text-[10px] text-gray-500">
                                  {formatCurrencyShorthand(record.priorMonthCents || 0)} &rarr; {formatCurrencyShorthand(record.trailCents)}/mo
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 bg-gray-800/50 border-t border-gray-700/40 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    {urgentCount > 0 && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                        {urgentCount} urgent
                      </span>
                    )}
                    {churnedCount > 0 && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                        {churnedCount} churned
                      </span>
                    )}
                    {earlierUnresolvedCount > 0 && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        {earlierUnresolvedCount} unresolved
                      </span>
                    )}
                    {warningCount > 0 && (
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                        {warningCount} warning{warningCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/reports/trail-drop-off');
                    }}
                    className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 transition-colors"
                  >
                    View All {totalAlerts}
                    <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                  </button>
                </div>
              </>
            ) : (
              /* Empty state */
              <div className="px-5 py-8 flex flex-col items-center justify-center text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3">
                  <Check className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-sm font-medium text-gray-300">Your trail book is healthy</p>
                <p className="text-xs text-gray-500 mt-1">
                  No drop-offs detected in the last 30 days
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WhoToCallWidget;
